import { useEffect, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  Box,
  Heading,
  Alert,
  AlertIcon,
  AlertDescription,
  useColorModeValue,
} from '@chakra-ui/react';

import Card from 'components/card/Card';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
  initializeForm,
  resetForm,
  useLegacyAutoFormLocked,
} from 'reducers/legacyAutoFormReducer';
import { apiGetLegacyAutoFormData } from 'api';
import { AuthContext } from 'contexts/AuthContext';
import { ROLE_ADMIN } from 'variables/constants';

import PersonalSubmissionSection from './sections/PersonalSubmissionSection';
import BinderIdentificationSection from './sections/BinderIdentificationSection';
import DocumentComplianceSection from './sections/DocumentComplianceSection';
import TelematicsSection from './sections/TelematicsSection';
import BillingSection from './sections/BillingSection';
import UnderwritingSection from './sections/UnderwritingSection';
import PaperCopySection from './sections/PaperCopySection';
import AdditionalNotesSection from './sections/AdditionalNotesSection';
import SubmitSection from './sections/SubmitSection';

interface LegacyAutoSubmissionViewProps {
  policyUuid: string;
}

const LegacyAutoSubmissionView = ({
  policyUuid,
}: LegacyAutoSubmissionViewProps) => {
  const dispatch = useAppDispatch();
  const { user } = useContext(AuthContext);
  const isDataSet = useAppSelector((state) => state.legacyAutoForm.isDataSet);
  const isLocked = useLegacyAutoFormLocked();

  const isAdmin = user?.role === ROLE_ADMIN;
  const canEditPremium = isAdmin && isLocked;

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const cardBg = useColorModeValue('white', 'navy.800');

  // Fetch existing form data
  const { data: initialData, isFetching } = useQuery({
    queryKey: ['legacy_auto_form', policyUuid],
    queryFn: () => apiGetLegacyAutoFormData(policyUuid),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    dispatch(resetForm());
  }, [policyUuid, dispatch]);

  useEffect(() => {
    if (isDataSet || isFetching || !initialData) return;

    // Initialize form with API data
    dispatch(initializeForm(initialData));
  }, [initialData, policyUuid, isDataSet, isFetching, dispatch]);

  if (!isDataSet) {
    return null;
  }

  return (
    <Box>
      {isLocked && isAdmin && (
        <Alert status="warning" borderRadius="md" mb="20px">
          <AlertIcon />
          <AlertDescription>
            <strong>
              This form has been submitted. As an admin, you may only edit the
              premium field. All other fields are locked.
            </strong>
          </AlertDescription>
        </Alert>
      )}
      {isLocked && !isAdmin && (
        <Alert status="info" borderRadius="md" mb="20px">
          <AlertIcon />
          <AlertDescription>
            <strong>
              This form has been completed and submitted. No further changes can
              be made.
            </strong>
          </AlertDescription>
        </Alert>
      )}

      <Card bg={cardBg} p={6} mb={6}>
        <Heading size="lg" color={textColor}>
          Non-Ontario Auto Submission
        </Heading>
      </Card>

      <PersonalSubmissionSection />
      <BinderIdentificationSection canEditPremium={canEditPremium} />
      <DocumentComplianceSection />
      <TelematicsSection />
      <BillingSection policyUuid={policyUuid} canEditPremium={canEditPremium} />
      <UnderwritingSection />
      <PaperCopySection />
      <AdditionalNotesSection />
      <SubmitSection policyUuid={policyUuid} canEditPremium={canEditPremium} />
    </Box>
  );
};

export default LegacyAutoSubmissionView;
